import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Polygon, Tooltip } from "react-leaflet";
import { OptionV2 } from "../../../components/FormControlsV2";
import { LeafletStaticMap } from "../../../components/Leaflet/LeafletStaticMap/Index";
import { FlyToBounds } from "../../../components/Leaflet/FlyToBounds/Index";
import Constants from "../../../constants/Index";
import "./styles.css";

type PastureMapPanelProps = {
    selectedFarms: OptionV2[];
    focusedFarmId: string | null;
    setFocusedFarmId: (id: string | null) => void;
    microAreas?: IMicroArea[];
    microAreasSelected: number[];
};

export function PastureMapPanel({ selectedFarms, focusedFarmId, setFocusedFarmId, microAreas, microAreasSelected }: PastureMapPanelProps) {
    const { t } = useTranslation();
    const focusedFarm = selectedFarms.find(farm => String(farm.value) === focusedFarmId) ?? selectedFarms[0];
    const farmCoords = (farm: OptionV2) => {
        const coords = farm.info?.map_coords ?? farm.info?.coords;
        return Array.isArray(coords) && coords.length ? coords : [];
    };
    const mapCoords = useMemo(() => focusedFarm ? farmCoords(focusedFarm) : [], [focusedFarm, selectedFarms]);
    useEffect(() => {
        if (!selectedFarms.some(farm => String(farm.value) === focusedFarmId)) {
            setFocusedFarmId(selectedFarms.length ? String(selectedFarms[0].value) : null);
        }
    }, [selectedFarms, focusedFarmId, setFocusedFarmId]);

    return (
                  <section className="pasture-map-panel" aria-label={t("pastureMap.title")}>
                    <header className="pasture-map-heading">
                      <div><span>{t("pastureMap.title")}</span><strong aria-live="polite">{focusedFarm?.label || t("reportLayout.none")}</strong></div>
                      <span className="pasture-map-count">{t("pastureMap.farmCount", { count: selectedFarms.length })}</span>
                    </header>
                    <div className="pasture-map-navigation" aria-label={t("pastureMap.navigation")}>
                      {selectedFarms.map(farm => <button type="button" key={String(farm.value)} aria-pressed={String(farm.value) === String(focusedFarm?.value)} onClick={() => setFocusedFarmId(String(farm.value))}>{farm.label}</button>)}
                    </div>
                    {mapCoords.length > 0 ? <div className="mapFilterManagement">
                      <LeafletStaticMap>
                        <FlyToBounds fitCoords={mapCoords}>
                          {selectedFarms.filter(farm => farmCoords(farm).length).map(farm => <Polygon
                            key={String(farm.value)}
                            positions={farmCoords(farm)}
                            dashArray={Constants.LEAFLET_DASHED_GREEN.dashArray}
                            color={Constants.LEAFLET_DASHED_GREEN.color}
                            fill={false}
                            eventHandlers={{ click: () => setFocusedFarmId(String(farm.value)) }}
                          ><Tooltip>{farm.label}</Tooltip></Polygon>)}
                          {microAreas?.filter(microarea => microAreasSelected?.includes(microarea.id) && selectedFarms.some(farm => String(farm.value) === String(microarea.farm?.id)) && microarea.map_coords?.length).map(microarea => <Polygon
                            key={microarea.id}
                            positions={microarea.map_coords}
                            color={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.color}
                            fillColor={Constants.LEAFLET_NORMAL_BLUE.fillColor}
                            fillOpacity={Constants.LEAFLET_UNFOCUS_TRANSLUCENT.fillOpacity}
                          ><Tooltip>{microarea.initials}</Tooltip></Polygon>)}
                        </FlyToBounds>
                      </LeafletStaticMap>
                    </div> : <div className="pasture-map-empty">{t(selectedFarms.length ? "pastureMap.noCoordinates" : "pastureMap.noFarms")}</div>}
                  </section>
    );
}
